/**
 * Printable documents built as standalone HTML strings (customer invoices, packing lists,
 * purchase orders). The caller builds escaped markup; these helpers open it for printing
 * ("Print or PDF") or download it as a file.
 */

/** Escapes text for documents built as HTML strings. */
export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Appended to a document's body so the print dialog opens once it has loaded. */
export const AUTO_PRINT_SCRIPT =
  '<script>window.addEventListener("load",function(){setTimeout(function(){window.print();},150);});</script>';

type PrintWindowOptions = {
  width?: number;
  height?: number;
};

/** How long the new window has to load the document before its blob URL is released. */
const PRINT_URL_LIFETIME_MS = 60_000;

/**
 * Opens the document in a new window (which prints itself when it carries AUTO_PRINT_SCRIPT).
 * The document is served from a blob URL, so nothing is written into the window by script.
 * Returns false when the browser blocked the pop-up, so the caller can say so.
 */
export function openPrintableDocument(html: string, options: PrintWindowOptions = {}): boolean {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  const printWindow = window.open(url, '_blank', `width=${options.width ?? 1180},height=${options.height ?? 820}`);
  if (!printWindow) {
    URL.revokeObjectURL(url);
    return false;
  }
  printWindow.focus();
  window.setTimeout(() => URL.revokeObjectURL(url), PRINT_URL_LIFETIME_MS);
  return true;
}

/** Saves the document as an .html file. */
export function downloadHtmlDocument(html: string, fileName: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
